import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/contact';
const HTTP_METHOD = 'post';

// ============================================================================
// Types
// ============================================================================

export type ContactUsArg = { email: string; message: string };

const ContactRequestError = createBackendApiError({
  domain: 'ContactRequest',
  endpoint: 'Create',
  path: ENDPOINT,
  identifierField: 'email',
} as const);

export type ContactRequestCreateError = BackendApiErrorUnion<typeof ContactRequestError>;

const postContactRequestFetch = (data: ContactUsArg) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/json', 'application/json')
      .fetch({ body: data });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        'contact.email': data.email,
      },
    })
  );

export const createContactRequest = (data: ContactUsArg) =>
  postContactRequestFetch(data).pipe(
    Effect.map((res) => res.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': () => Effect.void,
        404: (response) =>
          Effect.fail(new ContactRequestError.NotFoundError({ email: data.email, response })),
        422: ContactRequestError.createViolationError(data.email),
        orElse: (response) =>
          Effect.fail(new ContactRequestError.ApiRequestError({ email: data.email, response })),
      })
    ),
    Effect.tap(() =>
      logApiOperation(
        ApiModule.CustomerContactUs,
        'Debug',
        `Successfully created contact request for email: ${data.email}`
      )
    ),
    Effect.withSpan('api.customer.createContactRequest', {
      kind: 'internal',
      attributes: {
        'contact.email': data.email,
      },
    })
  );
